/**
 * Commodity Planner: view-only grouping of USA regions into one "USA" series.
 *
 * Series keys are "{year}_{origin}". Selection state always stays in per-region
 * keys; this transform only reshapes what is plotted, so toggling the group
 * checkbox never changes (or loses) the user's selected regions.
 */

export const USA_ORIGIN_LABEL = 'USA';

const YEAR_PREFIX = /^(\d{4})_(.+)$/;

export const splitSeriesKey = (key) => {
  const m = String(key ?? '').match(YEAR_PREFIX);
  return m ? { year: parseInt(m[1], 10), origin: m[2] } : { year: null, origin: String(key ?? '') };
};

export const usaGroupKeyForYear = (year) => `${year}_${USA_ORIGIN_LABEL}`;

export const isUsaGroupKey = (key) => {
  const { year, origin } = splitSeriesKey(key);
  return year != null && origin === USA_ORIGIN_LABEL;
};

const normalizeOrigin = (origin) => String(origin ?? '').trim().toLowerCase();

/** Lower-cased set of origin names flagged as USA regions. */
export const toUsaOriginSet = (usaOrigins) =>
  new Set((Array.isArray(usaOrigins) ? usaOrigins : []).map(normalizeOrigin).filter(Boolean));

export const isUsaOrigin = (origin, usaOriginSet) =>
  !!usaOriginSet && usaOriginSet.size > 0 && usaOriginSet.has(normalizeOrigin(origin));

/**
 * Per-region share of a USA total, in percent. Zero or invalid totals yield 0 for
 * every region (never NaN / Infinity).
 */
export const computeRegionShares = (regionValues) => {
  const entries = Object.entries(regionValues || {})
    .map(([region, value]) => [region, Number(value) || 0])
    .filter(([, value]) => value > 0);
  const total = entries.reduce((sum, [, value]) => sum + value, 0);
  return {
    total,
    regions: entries
      .map(([region, value]) => ({ region, value, percent: total > 0 ? (value / total) * 100 : 0 }))
      .sort((a, b) => b.value - a.value),
  };
};

/**
 * Merge the selected USA region series into one `{year}_USA` series per year.
 *
 * @param {Array<Object>} rows        Aggregated chart rows ({label, [seriesKey]: number, ...})
 * @param {string[]} selectedKeys     Selected series keys, in display order
 * @param {Set<string>} usaOriginSet  Lower-cased USA region names (see toUsaOriginSet)
 * @returns {{ rows, keys, usaKeys, regionKeysByGroup }}
 *   rows: rows with USA region columns replaced by `{year}_USA` totals plus a
 *         `usaBreakdown` map ({ [year]: { [region]: value } }) used by the tooltip.
 *   keys: selected keys with USA regions replaced by the group keys.
 *   regionKeysByGroup: { [groupKey]: string[] } original region keys per group.
 */
export const groupUsaSeries = (rows, selectedKeys, usaOriginSet) => {
  const regionKeysByGroup = {};
  const passthroughKeys = [];

  (selectedKeys || []).forEach((key) => {
    const { year, origin } = splitSeriesKey(key);
    if (year != null && isUsaOrigin(origin, usaOriginSet)) {
      const groupKey = usaGroupKeyForYear(year);
      if (!regionKeysByGroup[groupKey]) regionKeysByGroup[groupKey] = [];
      regionKeysByGroup[groupKey].push(key);
    } else {
      passthroughKeys.push(key);
    }
  });

  const groupKeys = Object.keys(regionKeysByGroup);
  if (groupKeys.length === 0) {
    return { rows, keys: [...(selectedKeys || [])], usaKeys: [], regionKeysByGroup };
  }

  const usaRegionKeys = new Set(groupKeys.flatMap((g) => regionKeysByGroup[g]));

  const nextRows = (rows || []).map((row) => {
    const next = {};
    Object.keys(row).forEach((k) => {
      if (!usaRegionKeys.has(k)) next[k] = row[k];
    });
    const breakdown = {};
    groupKeys.forEach((groupKey) => {
      const year = splitSeriesKey(groupKey).year;
      const regions = {};
      let total = 0;
      regionKeysByGroup[groupKey].forEach((regionKey) => {
        const value = Number(row[regionKey]) || 0;
        total += value;
        if (value > 0) regions[splitSeriesKey(regionKey).origin] = value;
      });
      next[groupKey] = total;
      breakdown[year] = regions;
    });
    next.usaBreakdown = breakdown;
    return next;
  });

  return {
    rows: nextRows,
    keys: [...passthroughKeys, ...groupKeys],
    usaKeys: groupKeys,
    regionKeysByGroup,
  };
};
